// module-header.tsx
// The shared module header band: one component, every routed module page.
// Built from the unified-header design handoff (quiet instrument treatment).
// The eyebrow is the module icon plus the module name. The old
// MEASURE / OPTIMIZE / TRACK taxonomy is gone from the copy layer entirely
// (UPM-150): group identity lives in the rail alone.
//
// Anatomy, top to bottom: breadcrumb row (with optional selector), title row
// (with optional action), description, opt-in tabs with a sliding accent rail.
// Optional scroll-collapse to a sticky mini-bar.
//
// The band carries breadcrumb, title and description ONLY. The stat strip was
// removed wholesale (UPM-144): page metrics belong in the page, not in the
// header. The prop is gone rather than merely unused so a band cannot quietly
// grow one back.
//
// Codebase adaptations vs the prototype, both deliberate:
// - Icons are Lucide components (matching nav-config, the icon source of
//   truth), not string names. The arrow-right "next step" affordance is
//   detected by identity against ArrowRight.
// - actionsSlot / selectorSlot accept a ReactNode so pages with rich existing
//   controls (run selector, export menus) keep their behavior while sitting
//   in the designed positions. Simple pages use the structured props.
//
// No data fetching here; all interactivity is host-provided callbacks.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ArrowRight, ChevronDown, type LucideIcon } from "lucide-react";
import "./module-header.css";
import "./shell-page.css";

export interface ModuleHeaderTab {
  value: string;
  label: string;
}

export interface ModuleHeaderAction {
  label: string;
  icon?: LucideIcon;
  variant?: "primary" | "secondary";
  onClick?: () => void;
  disabled?: boolean;
}

export interface ModuleHeaderSelector {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
}

export interface ModuleHeaderProps {
  /** Current page name, sentence case (e.g. "Brand Monitoring"). */
  page: string;
  /** The module's icon. Must match the left-nav icon (nav-config). */
  icon: LucideIcon;
  /** The module outcome, one line. Compact pages may use the page name. */
  title: string;
  /** Optional quiet subline. Clamps to one line under 560px band width. */
  description?: string;
  /** Optional primary/secondary action button. */
  action?: ModuleHeaderAction;
  /** Rich action cluster (existing menus); wins over `action` if both set. */
  actionsSlot?: ReactNode;
  /** Optional top-right ghost control (run/brand switcher). */
  selector?: ModuleHeaderSelector;
  /** Rich selector control; wins over `selector` if both set. */
  selectorSlot?: ReactNode;
  /** Optional tabs; when present the band closes with the sliding rail. */
  tabs?: ModuleHeaderTab[];
  /** Controlled active tab value. Uncontrolled if omitted. */
  activeTab?: string;
  onTabChange?: (value: string) => void;
  /** Utility/Manage pages: tighter vertical padding. */
  compact?: boolean;
  /** Scroll-collapse to a sticky mini-bar. Listens on the nearest
   *  [data-uh-scroll] ancestor, else window. */
  collapsible?: boolean;
}

export function ModuleHeader(props: ModuleHeaderProps) {
  const {
    page,
    icon: Icon,
    title,
    description,
    action,
    actionsSlot,
    selector,
    selectorSlot,
    tabs,
    activeTab,
    onTabChange,
    compact = false,
    collapsible = false,
  } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const rafRef = useRef<number | null>(null);

  const [collapsed, setCollapsed] = useState(false);
  const [internalTab, setInternalTab] = useState<string>(tabs?.[0]?.value ?? "");
  const [rail, setRail] = useState<{ left: number; width: number } | null>(null);

  const hasTabs = !!tabs && tabs.length > 0;
  const currentTab = activeTab ?? internalTab;
  const activeIndex = hasTabs
    ? Math.max(0, tabs.findIndex((t) => t.value === currentTab))
    : -1;

  // ── Sliding rail: measure the active tab; recompute on change, resize,
  //    and font load so the underline tracks real glyph widths.
  const measureRail = useCallback(() => {
    if (!hasTabs) return;
    const el = tabRefs.current[activeIndex];
    if (!el) return;
    const next = { left: el.offsetLeft, width: el.offsetWidth };
    setRail((prev) =>
      prev && prev.left === next.left && prev.width === next.width ? prev : next,
    );
  }, [hasTabs, activeIndex]);

  useLayoutEffect(() => {
    measureRail();
  }, [measureRail, tabs]);

  useEffect(() => {
    if (!hasTabs) return;
    const onResize = () => measureRail();
    window.addEventListener("resize", onResize);
    let cancelled = false;
    // Re-measure once webfonts settle (Inter changes glyph widths).
    if (typeof document !== "undefined" && "fonts" in document) {
      (document as Document & { fonts: { ready: Promise<unknown> } }).fonts.ready.then(
        () => {
          if (!cancelled) measureRail();
        },
      );
    }
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
    };
  }, [hasTabs, measureRail]);

  // ── Scroll-collapse: sticky band condenses past 16px of scroll.
  useEffect(() => {
    if (!collapsible) return;
    const root = rootRef.current;
    if (!root) return;
    const host: HTMLElement | Window =
      (root.closest("[data-uh-scroll]") as HTMLElement | null) ?? window;
    const read = () =>
      host === window ? window.scrollY : (host as HTMLElement).scrollTop;
    const onScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setCollapsed(read() > 16);
      });
    };
    host.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      host.removeEventListener("scroll", onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [collapsible]);

  const isCollapsed = collapsible && collapsed;

  const selectTab = (value: string) => {
    if (activeTab === undefined) setInternalTab(value);
    onTabChange?.(value);
  };

  const renderAction = () => {
    if (actionsSlot) return <>{actionsSlot}</>;
    if (!action) return null;
    const ActionIcon = action.icon;
    const trailing = ActionIcon === ArrowRight;
    return (
      <button
        type="button"
        className={
          action.variant === "secondary"
            ? "uh-action uh-action--secondary"
            : "uh-action"
        }
        onClick={action.onClick}
        disabled={action.disabled}
      >
        {ActionIcon && !trailing && (
          <span className="uh-action-icon">
            <ActionIcon size={16} strokeWidth={1.9} />
          </span>
        )}
        {action.label}
        {ActionIcon && trailing && (
          <span className="uh-action-icon uh-action-icon--trailing">
            <ActionIcon size={16} strokeWidth={2} />
          </span>
        )}
      </button>
    );
  };

  const renderSelector = () => {
    if (selectorSlot) return <>{selectorSlot}</>;
    if (!selector) return null;
    const SelIcon = selector.icon;
    return (
      <button type="button" className="uh-selector" onClick={selector.onClick}>
        <span className="uh-action-icon">
          <SelIcon size={15} strokeWidth={1.7} />
        </span>
        <span className="uh-selector-label">{selector.label}</span>
        <span className="uh-selector-chevron">
          <ChevronDown size={14} strokeWidth={1.9} />
        </span>
      </button>
    );
  };

  const bandClass = [
    "uh-band",
    compact ? "uh-band--compact" : "",
    hasTabs && !isCollapsed ? "uh-band--tabs" : "",
    collapsible ? "uh-band--collapsible" : "",
    isCollapsed ? "uh-band--collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="uh-wrap">
      <div ref={rootRef} className={bandClass}>
        {isCollapsed && (
          <div className="uh-mini">
            <span className="uh-mini-identity">
              <span className="uh-crumb-icon">
                <Icon size={14} strokeWidth={1.7} />
              </span>
              <span className="uh-mini-page">{page}</span>
            </span>
            {renderAction()}
          </div>
        )}

        <div style={{ display: isCollapsed ? "none" : "block" }}>
          <div className="uh-crumb-row">
            <span className="uh-crumb-group">
              <span className="uh-crumb-icon">
                <Icon size={14} strokeWidth={1.7} />
              </span>
              <span className="uh-crumb-page">{page}</span>
            </span>
            {renderSelector()}
          </div>

          <div className="uh-title-row">
            <h1 className="uh-title">{title}</h1>
            {renderAction()}
          </div>

          {description && <p className="uh-desc">{description}</p>}

          {hasTabs && (
            <div className="uh-tabs">
              {tabs.map((t, i) => (
                <button
                  key={t.value}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  className={
                    i === activeIndex ? "uh-tab uh-tab--active" : "uh-tab"
                  }
                  onClick={() => selectTab(t.value)}
                >
                  {t.label}
                </button>
              ))}
              {rail && (
                <span
                  className="uh-rail"
                  style={{ left: rail.left, width: rail.width }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
